import { Box, Menu, Text } from 'grommet';
import { toast } from 'react-toastify';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IUser } from '@interfaces/IUser';
import useAppContext from '@components/Template/Context/useAppContext';
import onSwitchUsers from '@components/ManageUsers/Helpers/onSwitchUsers';
import ConfirmModal from '@components/ConfirmModal/ConfirmModal';

export interface UserManageOptionsProps {
  user: IUser;
}

const UserManageOptions: React.FC<UserManageOptionsProps> = ({ user }): JSX.Element => {
  const [switching, setSwitching] = React.useState<boolean>(false);
  const changeSwitching = (): void => { setSwitching(!switching); };

  const navigate = useNavigate();

  const onUpdateEmailClick = (): void => {
    navigate(`/owner/updateUserEmail/${user.Guid}`);
  };

  const {
    dispatch,
  } = useAppContext();

  const switchUsers = async (): Promise<void> => {
    await onSwitchUsers(user.Email!)
      .then((data): void => {
        if (data.Success) {
          const value = data.Object as IUser;

          dispatch({
            type: 'changeFirstName',
            value: value.FirstName,
          });

          dispatch({
            type: 'changeLastName',
            value: value.LastName,
          });

          dispatch({
            type: 'changeEmail',
            value: value.Email,
          });

          dispatch({
            type: 'changeAuth',
            value: true,
          });

          dispatch({
            type: 'changeValidated',
            value: value.Validated,
          });

          dispatch({
            type: 'changeHigherLevel',
            value: value.HigherLevel,
          });

          dispatch({
            type: 'changeTheme',
            value: value.Theme,
          });

          dispatch({
            type: 'changeLanguage',
            value: value.Language,
          });

          dispatch({
            type: 'changeNickName',
            value: value.NickName,
          });

          toast.success('Switched Users');

          setSwitching(false);

          window.location.href = import.meta.env.VITE_API_URL;
        } else {
          toast.error('Failed to switch users');

          setSwitching(false);
        }
      })
      .catch((): void => {
        toast.error('Failed to switch users');

        setSwitching(false);
      });
  };

  return (
    <Box
      align="center"
    >
      <Menu
        dropProps={{
          align: { top: 'bottom', left: 'left' },
          elevation: 'xlarge',
        }}
        label={<Text weight="bold">{`${user.FirstName} ${user.LastName}`}</Text>}
        items={[
          [{ label: user.NickName }],
          [
            {
              label: 'Switch Users',
              title: 'Switch to this user account',
              disabled: switching,
              onClick: changeSwitching,
            },
            {
              label: 'Update Email',
              onClick: onUpdateEmailClick,
              title: 'Update the email address for this user',
            },
          ],
          [
            {
              label: 'Set Completed Modules',
            },
            {
              label: 'Set Completed Courses',
            }
          ],
        ]}
      />
      {
        (switching) && (
          <ConfirmModal
            title="Switch Users"
            text={`Are you sure you want to switch to user ${user.FirstName} ${user.LastName}?`}
            onClose={changeSwitching}
            onConfirm={switchUsers}
            confirmBtnText="Yes, Switch"
            closeBtnText="Cancel"
            closable
          />
        )
      }
    </Box>
  );
};

UserManageOptions.displayName = 'UserManageOptions';

export default UserManageOptions;
